import { isSchedulableDeviceStatus } from '@/utils/deviceStatus';

/** Active, new implant, and pending devices. Inactive and entered-in-error are left out. */
export function schedulableDeviceIds(
  devices: Array<{ id?: string | null; status?: unknown }> | null | undefined
): string[] {
  const ids = new Set<string>();
  for (const device of devices ?? []) {
    if (!device.id || !isSchedulableDeviceStatus(device.status)) continue;
    ids.add(device.id);
  }
  return [...ids];
}

/** True only when remote monitoring changes to off. A patient who is already off is left alone. */
export function remoteMonitoringTurnedOff(
  previous: boolean | null | undefined,
  next: boolean | null | undefined
): boolean {
  return next === false && previous !== false;
}

/** True only when remote monitoring changes to on. */
export function remoteMonitoringTurnedOn(
  previous: boolean | null | undefined,
  next: boolean | null | undefined
): boolean {
  return next === true && previous !== true;
}

const SCHEDULE_SETTING_FIELDS = [
  'isHfMonitored',
  'shouldOnlyMonitorHf',
  'shouldOnlyMonitorAlerts',
  'cycleIcdPm',
  'cycleHf',
  'cycleIlr',
  'inClinicBuffer'
] as const;

function sameScheduleSetting(previous: unknown, next: unknown): boolean {
  if (previous == null && next == null) return true;
  return previous === next;
}

/** Cycle, buffer, heart-failure, and alert-monitoring fields. Other edits do not refresh dates. */
export function scheduleSettingsChanged(
  previous: Partial<Record<(typeof SCHEDULE_SETTING_FIELDS)[number], unknown>> | null | undefined,
  next: Partial<Record<(typeof SCHEDULE_SETTING_FIELDS)[number], unknown>> | null | undefined
): boolean {
  return SCHEDULE_SETTING_FIELDS.some(
    (field) => !sameScheduleSetting(previous?.[field], next?.[field])
  );
}

/**
 * Parent account wins, then the clinic. Automated Scheduling is the other way around:
 * the clinic workflow wins, then the parent. This flag only chooses Create Billable
 * Schedule versus Edit Schedule.
 */
export function appointmentsManagedInRs(
  organization:
    | {
        isAppointmentManagedInRs?: boolean | null;
        parent?: { isAppointmentManagedInRs?: boolean | null } | null;
      }
    | null
    | undefined
): boolean {
  return (
    organization?.parent?.isAppointmentManagedInRs ??
    organization?.isAppointmentManagedInRs ??
    false
  );
}

/** Clinic workflow requirement. Rhythm owns the transmission schedule. */
export const AUTOMATED_SCHEDULING_REQUIREMENT = 'Automated Scheduling';

/**
 * Clinic value wins, then the parent account. Missing from the workflow means Rhythm does not
 * own the schedule, so the portal does not call the scheduler.
 */
export function clinicSchedulesAutomatically(
  organization:
    | {
        settings?: {
          routing?: { workflowRequirements?: string[] | null } | null;
        } | null;
        parent?: {
          settings?: {
            routing?: { workflowRequirements?: string[] | null } | null;
          } | null;
        } | null;
      }
    | null
    | undefined
): boolean {
  const own = organization?.settings?.routing?.workflowRequirements;
  const inherited = organization?.parent?.settings?.routing?.workflowRequirements;
  const requirements = own ?? inherited ?? [];
  return requirements.includes(AUTOMATED_SCHEDULING_REQUIREMENT);
}

/** Runs the scheduler once per device. A failure on one device does not stop the others. */
export async function runSchedulerForDevices(
  runScheduler: (body: { deviceId: string }) => { unwrap: () => Promise<unknown> },
  deviceIds: string[]
): Promise<number> {
  let failed = 0;
  for (const deviceId of deviceIds) {
    try {
      await runScheduler({ deviceId }).unwrap();
    } catch (error) {
      failed += 1;
      console.error('Scheduler did not run for device', deviceId, error);
    }
  }
  return failed;
}
